import { useState, useEffect, useCallback } from 'react';
import { Layers, Ruler, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import ConfirmDialog from '../../ui/ConfirmDialog';

/**
 * MultiSelectionProperties - Properties panel for multiple selected objects
 *
 * Displays and allows editing of bounding box:
 * - Number of selected objects
 * - Bounding box dimensions (Width and Height in cm) - EDITABLE
 * - Bounding box position (X, Y in cm) - EDITABLE
 * - Total area covered by bounding box (in m²) - READ-ONLY
 *
 * @param {object} selection - Fabric.js ActiveSelection object
 * @param {function} onUpdate - Callback when properties change
 * @param {function} onDelete - Callback when delete button is clicked
 * @param {number} pixelsPerCm - Conversion factor (default: 1 pixel = 1 cm)
 * @param {number} gridSize - Grid size in pixels for snap-aware increments
 * @param {boolean} snapEnabled - Whether snap-to-grid is enabled
 */
const MultiSelectionProperties = ({ selection, onUpdate, onDelete, pixelsPerCm = 1, gridSize = 50, snapEnabled = true }) => {
  const [boundingBox, setBoundingBox] = useState({ width: 0, height: 0, x: 0, y: 0 });
  const [objectCount, setObjectCount] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Calculate snap increment in cm (use 1 if snap is disabled)
  const snapIncrementCm = snapEnabled ? Math.round(gridSize / pixelsPerCm) : 1;

  // Helper function: Snap value to nearest grid boundary
  const snapToGrid = (value, increment) => {
    return Math.round(value / increment) * increment;
  };

  // Helper function: Get next grid boundary (for increment)
  const getNextGridBoundary = (value, increment) => {
    const snapped = snapToGrid(value, increment);
    // If already on grid, go to next boundary
    if (snapped === value) {
      return value + increment;
    }
    // If between boundaries, go to next boundary
    return snapped + (value < snapped ? 0 : increment);
  };

  // Helper function: Get previous grid boundary (for decrement)
  const getPrevGridBoundary = (value, increment) => {
    const snapped = snapToGrid(value, increment);
    // If already on grid, go to previous boundary
    if (snapped === value) {
      return value - increment;
    }
    // If between boundaries, go to previous boundary
    return snapped - (value > snapped ? 0 : increment);
  };

  // Calculate bounding box from selection
  const calculateBoundingBox = useCallback(() => {
    if (!selection || !selection._objects || selection._objects.length === 0) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    selection._objects.forEach((obj) => {
      // Convert to absolute canvas coordinates
      const absoluteLeft = selection.left + obj.left;
      const absoluteTop = selection.top + obj.top;

      // Handle different object types (rectangles have width/height, ellipses have rx/ry)
      let width, height;
      if (obj.type === 'ellipse') {
        width = obj.rx * 2 * obj.scaleX;
        height = obj.ry * 2 * obj.scaleY;
      } else {
        width = obj.width * obj.scaleX;
        height = obj.height * obj.scaleY;
      }

      minX = Math.min(minX, absoluteLeft);
      minY = Math.min(minY, absoluteTop);
      maxX = Math.max(maxX, absoluteLeft + width);
      maxY = Math.max(maxY, absoluteTop + height);
    });

    // Convert to cm and round
    const widthCm = Math.round((maxX - minX) / pixelsPerCm);
    const heightCm = Math.round((maxY - minY) / pixelsPerCm);
    const xCm = Math.round(minX / pixelsPerCm);
    const yCm = Math.round(minY / pixelsPerCm);

    return { width: widthCm, height: heightCm, x: xCm, y: yCm };
  }, [selection, pixelsPerCm]);

  // Update bounding box when selection changes or is modified
  useEffect(() => {
    if (!selection) return;

    const updateBoundingBox = () => {
      const newBoundingBox = calculateBoundingBox();
      setBoundingBox(newBoundingBox);
      setObjectCount(selection._objects?.length || 0);
    };

    // Initial update
    updateBoundingBox();

    // Listen for modifications (when user drags/scales objects directly)
    const handleModified = () => {
      updateBoundingBox();
    };

    selection.on('modified', handleModified);
    selection.on('scaling', handleModified);
    selection.on('moving', handleModified);
    selection.on('rotating', handleModified);

    return () => {
      selection.off('modified', handleModified);
      selection.off('scaling', handleModified);
      selection.off('moving', handleModified);
      selection.off('rotating', handleModified);
    };
  }, [selection, pixelsPerCm, calculateBoundingBox]);

  // Handle dimension change (width/height)
  const handleDimensionChange = (field, value) => {
    // Handle empty input - allow clearing the field
    if (value === '') {
      setBoundingBox(prev => ({
        ...prev,
        [field]: ''
      }));
      return;
    }

    const numValue = parseInt(value, 10);

    // Reject invalid input (non-numbers or zero/negative)
    if (isNaN(numValue) || numValue <= 0) return;

    if (!selection) return;

    // Update the input value immediately for responsive UI
    setBoundingBox(prev => ({
      ...prev,
      [field]: numValue
    }));

    // Calculate current dimensions using the existing boundingBox state
    const currentBox = calculateBoundingBox();

    // Calculate the target dimension in pixels
    const targetPx = numValue * pixelsPerCm;

    // Get current dimension in pixels
    const currentDimensionCm = field === 'width' ? currentBox.width : currentBox.height;
    const currentDimensionPx = currentDimensionCm * pixelsPerCm;

    // Calculate absolute scale factor (not compound!)
    const absoluteScale = targetPx / currentDimensionPx;

    // Apply absolute scale to the selection. Use Fabric's set() (a method call)
    // rather than direct property assignment, which the immutability rule flags
    // as mutating the `selection` prop.
    if (field === 'width') {
      selection.set({ scaleX: absoluteScale });
    } else {
      selection.set({ scaleY: absoluteScale });
    }

    selection.setCoords();

    if (onUpdate) onUpdate();
  };

  // Handle position change (x/y)
  const handlePositionChange = (field, value) => {
    // Allow empty input or just minus sign for editing
    if (value === '' || value === '-') {
      setBoundingBox(prev => ({
        ...prev,
        [field]: value
      }));
      return;
    }

    // Parse value - allow negative numbers
    const numValue = parseInt(value, 10);

    // Reject invalid input
    if (isNaN(numValue)) return;

    const currentBox = calculateBoundingBox();

    if (!selection) return;

    // Update the input value immediately for responsive UI
    setBoundingBox(prev => ({
      ...prev,
      [field]: numValue
    }));

    // Calculate delta movement
    const deltaX = field === 'x' ? (numValue - currentBox.x) * pixelsPerCm : 0;
    const deltaY = field === 'y' ? (numValue - currentBox.y) * pixelsPerCm : 0;

    // Move the entire selection
    selection.set({
      left: selection.left + deltaX,
      top: selection.top + deltaY,
    });

    selection.setCoords();

    if (onUpdate) onUpdate();
  };

  // Handle increment button click (dimension fields: width/height)
  const handleDimensionIncrement = (field) => {
    const currentValue = boundingBox[field];
    if (currentValue === '' || isNaN(currentValue)) return;

    const nextValue = snapEnabled
      ? getNextGridBoundary(currentValue, snapIncrementCm)
      : currentValue + 1;

    handleDimensionChange(field, nextValue.toString());
  };

  // Handle decrement button click (dimension fields: width/height)
  const handleDimensionDecrement = (field) => {
    const currentValue = boundingBox[field];
    if (currentValue === '' || isNaN(currentValue)) return;

    const prevValue = snapEnabled
      ? getPrevGridBoundary(currentValue, snapIncrementCm)
      : currentValue - 1;

    // Don't allow dimensions less than 1
    if (prevValue < 1) return;

    handleDimensionChange(field, prevValue.toString());
  };

  // Handle increment button click (position fields: x/y)
  const handlePositionIncrement = (field) => {
    const currentValue = boundingBox[field];
    if (currentValue === '' || currentValue === '-' || isNaN(currentValue)) return;

    const nextValue = snapEnabled
      ? getNextGridBoundary(currentValue, snapIncrementCm)
      : currentValue + 1;

    handlePositionChange(field, nextValue.toString());
  };

  // Handle decrement button click (position fields: x/y)
  const handlePositionDecrement = (field) => {
    const currentValue = boundingBox[field];
    if (currentValue === '' || currentValue === '-' || isNaN(currentValue)) return;

    const prevValue = snapEnabled
      ? getPrevGridBoundary(currentValue, snapIncrementCm)
      : currentValue - 1;

    handlePositionChange(field, prevValue.toString());
  };

  // Calculate total area covered by bounding box in m²
  const areaSqm = ((boundingBox.width * boundingBox.height) / 10000).toFixed(2);

  return (
    <div className="p-4 space-y-4">
      {/* Multi-Selection Info */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <div className="flex items-center gap-2 text-emerald-700">
          <Layers className="w-4 h-4" />
          <span className="text-sm font-semibold">
            {objectCount} {objectCount === 1 ? 'Objekt' : 'Objekte'} ausgewählt
          </span>
        </div>
        <p className="text-xs text-emerald-600 mt-1">
          Begrenzungsrahmen aller Objekte
        </p>
      </div>

      {/* Bounding Box Dimensions Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-gray-700 border-b border-gray-200 pb-2">
          <Ruler className="w-4 h-4" />
          <span className="text-sm font-semibold">Abmessungen (Begrenzungsrahmen)</span>
        </div>

        {/* Width and Height Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Width */}
          <div className="space-y-1">
            <label htmlFor="multi-selection-width" className="text-xs text-gray-600">
              Breite
            </label>
            <div className="flex items-center gap-1">
              <div className="flex items-center flex-1">
                <input
                  id="multi-selection-width"
                  type="number"
                  value={boundingBox.width}
                  onChange={(e) => handleDimensionChange('width', e.target.value)}
                  min="1"
                  aria-label="Breite"
                  className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="flex flex-col border-y border-r border-gray-300 rounded-r">
                  <button
                    onClick={() => handleDimensionIncrement('width')}
                    className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    aria-label="Breite erhöhen"
                    type="button"
                  >
                    <ChevronUp size={12} className="text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDimensionDecrement('width')}
                    className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 transition-colors border-t border-gray-300"
                    aria-label="Breite verringern"
                    type="button"
                  >
                    <ChevronDown size={12} className="text-gray-600" />
                  </button>
                </div>
              </div>
              <span className="text-xs text-gray-500">cm</span>
            </div>
          </div>

          {/* Height */}
          <div className="space-y-1">
            <label htmlFor="multi-selection-height" className="text-xs text-gray-600">
              Höhe
            </label>
            <div className="flex items-center gap-1">
              <div className="flex items-center flex-1">
                <input
                  id="multi-selection-height"
                  type="number"
                  value={boundingBox.height}
                  onChange={(e) => handleDimensionChange('height', e.target.value)}
                  min="1"
                  aria-label="Höhe"
                  className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="flex flex-col border-y border-r border-gray-300 rounded-r">
                  <button
                    onClick={() => handleDimensionIncrement('height')}
                    className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    aria-label="Höhe erhöhen"
                    type="button"
                  >
                    <ChevronUp size={12} className="text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDimensionDecrement('height')}
                    className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 transition-colors border-t border-gray-300"
                    aria-label="Höhe verringern"
                    type="button"
                  >
                    <ChevronDown size={12} className="text-gray-600" />
                  </button>
                </div>
              </div>
              <span className="text-xs text-gray-500">cm</span>
            </div>
          </div>
        </div>

        {/* Area (read-only, styled like single selection) */}
        <div className="bg-gray-50 rounded p-3 border border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-600">Fläche:</span>
            <span className="text-sm font-semibold text-emerald-600">
              {areaSqm} m²
            </span>
          </div>
        </div>
      </div>

      {/* Position Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-gray-700 border-b border-gray-200 pb-2">
          <span className="text-sm font-semibold">Position (Begrenzungsrahmen)</span>
        </div>

        {/* X and Y Position Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* X Position */}
          <div className="space-y-1">
            <label htmlFor="multi-selection-x" className="text-xs text-gray-600">
              X (Links)
            </label>
            <div className="flex items-center gap-1">
              <div className="flex items-center flex-1">
                <input
                  id="multi-selection-x"
                  type="number"
                  value={boundingBox.x}
                  onChange={(e) => handlePositionChange('x', e.target.value)}
                  aria-label="X (Links)"
                  className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="flex flex-col border-y border-r border-gray-300 rounded-r">
                  <button
                    onClick={() => handlePositionIncrement('x')}
                    className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    aria-label="X erhöhen"
                    type="button"
                  >
                    <ChevronUp size={12} className="text-gray-600" />
                  </button>
                  <button
                    onClick={() => handlePositionDecrement('x')}
                    className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 transition-colors border-t border-gray-300"
                    aria-label="X verringern"
                    type="button"
                  >
                    <ChevronDown size={12} className="text-gray-600" />
                  </button>
                </div>
              </div>
              <span className="text-xs text-gray-500">cm</span>
            </div>
          </div>

          {/* Y Position */}
          <div className="space-y-1">
            <label htmlFor="multi-selection-y" className="text-xs text-gray-600">
              Y (Oben)
            </label>
            <div className="flex items-center gap-1">
              <div className="flex items-center flex-1">
                <input
                  id="multi-selection-y"
                  type="number"
                  value={boundingBox.y}
                  onChange={(e) => handlePositionChange('y', e.target.value)}
                  aria-label="Y (Oben)"
                  className="w-full px-2 py-1 text-sm text-right border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="flex flex-col border-y border-r border-gray-300 rounded-r">
                  <button
                    onClick={() => handlePositionIncrement('y')}
                    className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    aria-label="Y erhöhen"
                    type="button"
                  >
                    <ChevronUp size={12} className="text-gray-600" />
                  </button>
                  <button
                    onClick={() => handlePositionDecrement('y')}
                    className="px-1 py-0.5 hover:bg-gray-100 active:bg-gray-200 transition-colors border-t border-gray-300"
                    aria-label="Y verringern"
                    type="button"
                  >
                    <ChevronDown size={12} className="text-gray-600" />
                  </button>
                </div>
              </div>
              <span className="text-xs text-gray-500">cm</span>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Button */}
      <div className="pt-4">
        <button
          onClick={() => setShowDeleteDialog(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 border border-red-300 text-red-700 rounded hover:bg-red-100 hover:border-red-400 transition-all font-medium text-sm"
        >
          <Trash2 size={16} />
          Auswahl löschen
        </button>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onConfirm={() => {
          if (onDelete && selection && selection._objects) {
            // Copy the objects array first to avoid modifying while iterating
            const objectsToDelete = [...selection._objects];

            // Remove all objects from canvas
            objectsToDelete.forEach(obj => {
              if (selection.canvas) {
                selection.canvas.remove(obj);
              }
            });

            // Clear the active selection
            if (selection.canvas) {
              selection.canvas.discardActiveObject();
              selection.canvas.requestRenderAll();
            }
          }
          setShowDeleteDialog(false);
        }}
        onCancel={() => setShowDeleteDialog(false)}
        title="Auswahl löschen"
        message={`Möchten Sie alle ${objectCount} ausgewählten Objekte wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </div>
  );
};

export default MultiSelectionProperties;
